import React, { useEffect, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import type { RouteComponentProps } from '@reach/router'
import {
  AlbumsDocument,
  AlbumBatchPrintFilesDocument,
  AlbumPrintFilesDocument,
  UpdateDestinationMetadataDocument,
} from './graphql/__generated__'

interface AlbumEntry {
  orderId: string
  orderName: string
  destinationId: string
  albumDesignId: string
  quantity: number
  state: string
  pageCount: number
  pagesWithPhotos: number
  pagesWithVideos: number
  thumbnailURL: string | null
  reviewedAt: string | null
  printedAt: string | null
}

function extractAlbums(data: any): AlbumEntry[] {
  const albums: AlbumEntry[] = []
  if (!data?.albumOrders) return albums

  for (const order of data.albumOrders) {
    if (!order.designs) continue
    for (const dwd of order.designs) {
      if (!dwd.destinations) continue
      for (const dest of dwd.destinations) {
        if (!dest.albumDesign) continue
        const ad = dest.albumDesign
        const pages = ad.pages || []
        let meta: any = {}
        try { if (dest.metadataJSON) meta = JSON.parse(dest.metadataJSON) } catch(e) {}

        albums.push({
          orderId: order.id,
          orderName: order.name,
          destinationId: dest.id,
          albumDesignId: ad.id,
          quantity: dest.quantity,
          state: dest.state,
          pageCount: pages.length,
          pagesWithPhotos: pages.filter((p: any) => p.photoProductionURL).length,
          pagesWithVideos: pages.filter((p: any) => p.videoMediaId).length,
          thumbnailURL: pages.find((p: any) => p.photoThumbnailURL)?.photoThumbnailURL || null,
          reviewedAt: meta.albumReviewedAt || null,
          printedAt: meta.albumPrintedAt || null,
        })
      }
    }
  }

  return albums.sort((a, b) => +a.orderName - +b.orderName)
}

export default function Albums(_: RouteComponentProps) {
  const [loadAlbums, { data, error, loading, refetch }] = useLazyQuery(AlbumsDocument, {
    fetchPolicy: 'network-only',
    pollInterval: 10_000,
  })
  useEffect(() => { loadAlbums() }, [])

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [generating, setGenerating] = useState(false)

  const [fetchBatchPrintFiles] = useLazyQuery(AlbumBatchPrintFilesDocument, { fetchPolicy: 'network-only' })
  const [fetchPrintFiles] = useLazyQuery(AlbumPrintFilesDocument, { fetchPolicy: 'network-only' })
  const [updateMetadata] = useMutation(UpdateDestinationMetadataDocument)

  if (error) return <p className="p-4">Error loading albums.</p>
  if (!data) return <p className="p-4">Loading...</p>

  const albums = extractAlbums(data)
  const needsPrint = albums.filter(a => !a.printedAt)
  const printed = albums.filter(a => a.printedAt)

  const toggleSelect = (albumDesignId: string) => {
    const next = new Set(selected)
    if (next.has(albumDesignId)) next.delete(albumDesignId)
    else next.add(albumDesignId)
    setSelected(next)
  }

  const selectAllUnprinted = () => {
    if (selected.size === needsPrint.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(needsPrint.map(a => a.albumDesignId)))
    }
  }

  const setMeta = async (album: AlbumEntry, key: string, value: any) => {
    await updateMetadata({
      variables: {
        destinationID: album.destinationId,
        metadataJSON: JSON.stringify({ [key]: value }),
      },
    })
    refetch?.()
  }

  const generateBatchZip = async () => {
    if (selected.size === 0) return
    setGenerating(true)
    try {
      if (selected.size === 1) {
        const result = await fetchPrintFiles({ variables: { albumDesignId: [...selected][0] } })
        if (result.data?.albumPrintFilesURL) window.open(result.data.albumPrintFilesURL, '_blank')
      } else {
        const result = await fetchBatchPrintFiles({ variables: { albumDesignIds: [...selected] } })
        if (result.data?.albumBatchPrintFilesURL) window.open(result.data.albumBatchPrintFilesURL, '_blank')
      }
    } catch (e) {
      console.error('Error generating print files', e)
    } finally {
      setGenerating(false)
    }
  }

  const refreshButton = (
    <button
      className={"ml-2 text-2xl bg-gray-200 hover:bg-gray-300 rounded-full p-1 h-6 w-6 " +
                 (loading ? " bg-gray-700 text-white" : "")}
      style={{ lineHeight: '0' }}
      onClick={() => loadAlbums()}>
      <span className="relative top-[-.25rem]">{'\u21BB'}</span>
    </button>
  )

  const renderRow = (album: AlbumEntry) => {
    const isSelected = selected.has(album.albumDesignId)

    return (
      <li key={album.albumDesignId}
          className={"grid grid-cols-12 mb-2 items-center " +
            (album.printedAt ? 'bg-green-100 ' : '') +
            (isSelected ? 'bg-blue-50 ' : '')}>
        <div>
          <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(album.albumDesignId)} />
        </div>

        <div className="py-1 group relative">
          {album.thumbnailURL
            ? <img className="w-10 h-10 object-cover inline-block" src={album.thumbnailURL} />
            : <div className="w-10 h-10 bg-gray-200 inline-block" />}
        </div>

        <div className="py-2">{album.orderName}</div>
        <div className="py-2">{album.quantity}</div>
        <div className="py-2">{album.pageCount}</div>
        <div className="py-2">{album.pagesWithPhotos}</div>
        <div className="py-2">{album.pagesWithVideos}</div>

        <div className="py-2">
          {album.reviewedAt
            ? <button className="bg-green-300 p-1 px-2 text-sm"
                onClick={() => setMeta(album, 'albumReviewedAt', null)}>Reviewed</button>
            : <button className="bg-gray-200 p-1 px-2 text-sm"
                onClick={() => setMeta(album, 'albumReviewedAt', new Date().toISOString())}>Review</button>}
        </div>

        <div className="py-2">
          {album.printedAt
            ? <button className="bg-green-300 p-1 px-2 text-sm"
                onClick={() => setMeta(album, 'albumPrintedAt', null)}>Printed</button>
            : <button className="bg-gray-200 p-1 px-2 text-sm"
                onClick={() => setMeta(album, 'albumPrintedAt', new Date().toISOString())}>Print</button>}
        </div>

        <div className="col-span-3 py-2 space-x-2">
          <button
            className="bg-gray-200 p-1 px-2 text-sm"
            onClick={async () => {
              const result = await fetchPrintFiles({ variables: { albumDesignId: album.albumDesignId } })
              if (result.data?.albumPrintFilesURL) window.open(result.data.albumPrintFilesURL, '_blank')
            }}>
            ZIP
          </button>
        </div>
      </li>
    )
  }

  return (
    <div className="space-y-12 pb-96">
      <div>
        <h1 className="text-lg mb-4">
          Ready for Print ({needsPrint.length})
          {refreshButton}
        </h1>

        <div className="mb-4 space-x-2">
          <button
            className="bg-blue-500 text-white p-2 px-4 disabled:opacity-50"
            disabled={selected.size === 0 || generating}
            onClick={generateBatchZip}>
            {generating ? 'Generating...' : `Download Print Files (${selected.size})`}
          </button>
        </div>

        <div className="pl-4">
          <ul>
            <li className="grid grid-cols-12 font-bold mb-4 pb-1 border-b">
              <div>
                <input type="checkbox"
                  checked={selected.size === needsPrint.length && needsPrint.length > 0}
                  onChange={selectAllUnprinted} />
              </div>
              <div>Preview</div>
              <div>Order</div>
              <div>Qty</div>
              <div>Pages</div>
              <div>Photos</div>
              <div>Videos</div>
              <div>Reviewed</div>
              <div>Printed</div>
              <div className="col-span-3">Actions</div>
            </li>
            {needsPrint.map(renderRow)}
            {needsPrint.length === 0 && <p className="text-gray-500 py-4">No albums awaiting print.</p>}
          </ul>
        </div>
      </div>

      {printed.length > 0 && (
        <div>
          <h1 className="text-lg mb-4">Printed ({printed.length})</h1>
          <div className="pl-4">
            <ul>
              <li className="grid grid-cols-12 font-bold mb-4 pb-1 border-b">
                <div />
                <div>Preview</div>
                <div>Order</div>
                <div>Qty</div>
                <div>Pages</div>
                <div>Photos</div>
                <div>Videos</div>
                <div>Reviewed</div>
                <div>Printed</div>
                <div className="col-span-3">Actions</div>
              </li>
              {printed.map(renderRow)}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
